import { humanize } from './format';
import { formatNumber } from '../../utils/numberFormat';

/**
 * The one place GTM pages name a signal or a channel, so the coverage table,
 * the probability table, the account journey and Data health all say the same
 * thing. Mirrors SIGNAL_TYPES and CHANNELS in
 * packages/api/src/modules/gtm-signal-ops/registers.ts; anything not listed
 * falls back to a humanised slug.
 */
export const SIGNAL_LABELS: Record<string, string> = {
  pricing_page_view: 'Pricing page viewed',
  comparison_page_view: 'Competitor comparison page read',
  solution_page_view: 'Solution or bid-desk page viewed',
  case_study_view: 'Case study read',
  content_page_view: 'Article or glossary read',
  anonymous_company_visit: 'Company-level site visit',
  repeat_visit: 'Repeat website visit',
  buying_committee_forming: 'Several people from one company visiting',
  trial_form_submitted: 'Trial or pricing form submitted',
  demo_form_submitted: 'Demo requested on the website',
  demo_booked: 'Demo booked',
  linkedin_ad_served: 'LinkedIn ad served',
  linkedin_ad_clicked: 'LinkedIn ad clicked',
  linkedin_post_engagement: 'LinkedIn post engagement',
  competitor_post_engagement: 'Engaged with a competitor’s or influencer’s post',
  linkedin_keyword_post: 'Posted or engaged on a tracked keyword',
  newsletter_click: 'Newsletter or nurture click',
  webinar_registered: 'Webinar registered',
  webinar_attended: 'Webinar attended',
  event_attended: 'Event or booth attendance',
  g2_category_intent: 'G2 category or comparison view',
  sequence_sent: 'Sequence email sent',
  sequence_opened: 'Sequence email opened',
  sequence_clicked: 'Sequence link clicked',
  sequence_replied: 'Sequence replied',
  linkedin_connection_accepted: 'LinkedIn connection accepted',
  linkedin_outbound_replied: 'LinkedIn outbound replied',
  inbound_email_reply: 'Inbound email reply to a rep',
  meeting_held: 'Meeting held',
  tender_published: 'Matching tender published',
  tender_awarded_competitor: 'Tender awarded to a competitor',
  job_change_into_bid_role: 'Job change into a bid or tender role',
  hiring_bid_role: 'Hiring into a bid or tender role',
  named_on_internal_call: 'Named on an internal call',
  renewal_window_open: 'Renewal window opening',
  usage_decline: 'App usage declining',
  usage_spike: 'Usage spike or seat ceiling hit',
  app_login: 'Platform login',
};

export const CHANNEL_LABELS: Record<string, string> = {
  'google-ads': 'Google Ads',
  'bing-ads': 'Microsoft Ads',
  'linkedin-ads': 'LinkedIn Ads',
  'meta-ads': 'Meta Ads',
  'email-outbound': 'Email outbound',
  'linkedin-outbound': 'LinkedIn outbound',
  'sdr-calling': 'SDR calling',
  seo: 'SEO',
  geo: 'GEO (AI answer engines)',
  website: 'Website activity',
  newsletter: 'Newsletter & nurture',
  'linkedin-organic': 'LinkedIn organic',
  'referral-partner': 'Partner referral',
  'word-of-mouth': 'Word of mouth',
  webinar: 'Webinars',
  'trade-show': 'Trade shows & booths',
  'review-sites': 'Review sites (G2)',
  'tender-feed': 'Tender feed',
  'job-postings': 'Hiring & job changes',
  internal: 'Internal (named on a call)',
  product: 'Product usage',
  crm: 'CRM lifecycle',
  unattributed: 'No channel',
};

export function signalLabel(type: string | null | undefined, fallback?: string): string {
  if (!type) return 'No signal';
  return SIGNAL_LABELS[type] ?? fallback ?? humanize(type);
}

export function channelLabel(channel: string | null | undefined): string {
  if (!channel) return '';
  return CHANNEL_LABELS[channel] ?? humanize(channel);
}

/**
 * A company name as people say it: drops the numeric record suffix some sources
 * append ("Aramark_55327" → "Aramark") and turns leftover underscores into spaces.
 */
export function displayCompanyName(raw: string | null | undefined): string {
  if (!raw) return '';
  // Registry / CRM-export ids glued on by the source: "PFH Technology Group_17679",
  // "Arup _4744", "Ernst and Young_86592_(Main Account)".
  // CRM account-type tags glued on in Attio: "Accenture Limited (Primary Account)", "(Main Account)", "(HQ)".
  return (
    raw
      .replace(/\s*_\d+(?:_.*)?$/, '')
      .replace(/_/g, ' ')
      .replace(/\s*\((?:primary|main|parent|global|master|key)?\s*(?:account|acct|hq|headquarters)\)\s*$/i, '')
      .trim() || raw
  );
}

/** Match review pairs left out of the queue: one wording on Match review's tile and footer and on Data health. */
export function leftOutText(n: number): string {
  return `${formatNumber(n)} pair${n === 1 ? '' : 's'} with nothing to decide left out`;
}
